import {
  Component,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
} from '@angular/material/dialog';
import { MyLog } from '@common/common';
import { Subscription } from 'rxjs';
import { DataService } from '@data/data.service';
import { NotificationService } from '@notification/notification.service';
import {
  UserDeviceChangeEvent,
  UserDeviceService,
} from '@services/user-device.service';
import { isJsObject, isNonEmptyString, parseDec, unsub } from '@util/utils';
import { elemHeight, setElementSizes } from '@util/visual-utils';
import { getClientRect } from '@util/web-utils';
import { DocumentViewBase } from '../../document-view-base';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { DocumentProgressBarComponent } from '../../document-progress-bar/document-progress-bar.component';
import { DocumentPaginatorComponent } from '../../document-paginator/document-paginator.component';
import { getDialogDims } from '@util/angular.utils';

function ne(elem: any) {
  return elem ? elem.nativeElement : null;
}

@Component({
  selector: 'app-view-document-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    DocumentProgressBarComponent,
    DocumentPaginatorComponent,
  ],
  templateUrl: './view-document-dialog.component.html',
})
export class ViewDocumentDialogComponent
  extends DocumentViewBase
  implements OnInit {

  imageLoadFailed = false;

  isImageDoc = false;

  isVideo = false;

  isPdf = false;

  isLoading = false;

  imageLoading = false;

  loadingDataTxt = 'Daten werden geladen';

  loadingPageTxt = 'Seite wird geladen';

  videoType = '';
  videoSrc = '';
  imgUrl: any;
  data: any;

  imageHeight = 0;
  imageWidth = 0;

  videoHeight = 0;
  videoWidth = 0;

  zoomMode = 1;

  scale = 100;

  buttons: any[] = [];

  udsSub!: Subscription;

  _actions!: ElementRef;
  @ViewChild('actions') set actions(v: ElementRef) {
    this._actions = v;
    this.setSizes();
  }

  _title!: ElementRef;
  @ViewChild('title') set title(v: ElementRef) {
    this._title = v;
    this.setSizes();
  }

  _content!: ElementRef;
  @ViewChild('cnt') set cnt(v: ElementRef) {
    this._content = v;
    this.setSizes();
  }

  _wrapper!: ElementRef;
  @ViewChild('wrapper') set wrapper(v: ElementRef) {
    this._wrapper = v;
    this.setSizes();
  }

  _imgView!: ElementRef;
  @ViewChild('imageview') set imageview(v: ElementRef) {
    this._imgView = v;

    if (v?.nativeElement) {
      const elem = v.nativeElement;
      elem.addEventListener('load', (ev: Event) => {
        const target = ev.target as HTMLImageElement;
        if (target) {
          this.imageHeight = target.naturalHeight;
          this.imageWidth = target.naturalWidth;
        }
        this.fitSizes();
        elem.classList.remove('hidden');
      });
    }
  }

  removeVideoEL?: () => void;

  _video!: ElementRef;
  @ViewChild('vs') set vs(v: ElementRef) {
    this._video = v;

    const elem = v?.nativeElement;

    if (elem) {
      this.removeVideoEL && this.removeVideoEL();

      const el = (ev: any) => {
        this.videoWidth = elem.videoWidth;
        this.videoHeight = elem.videoHeight;
        this.fitSizes();
      };

      elem.addEventListener('loadedmetadata', el);

      this.removeVideoEL = () => {
        elem.removeEventListener('loadedmetadata', el);
      };
    }
  }

  constructor(
    public dialogRef: MatDialogRef<ViewDocumentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private dataService: DataService,
    notificationService: NotificationService,
    private userDeviceService: UserDeviceService
  ) {
    super(notificationService);
  }

  ngOnInit(): void {
    this.data = this.dialogData.order;

    MyLog.log('DOC:INIT:', this.data);

    if (isJsObject(this.data)) {
      if (this.data.isvideo) {
        this.videoType = this.data.mediaType;
        this.videoSrc = this.data.url;
        MyLog.log('IS VIDEO');
      }

      if (this.data.isimage) {
        this.imgUrl = this.data.url;
        MyLog.log('IS IMAGE');
      }

      this.isVideo = !!this.data.isvideo;
      this.isImageDoc = !!this.data.isimage;

      this.isPdf = this.data.ispdf;
    }

    this.udsSub = this.userDeviceService
      .change()
      .subscribe((change: UserDeviceChangeEvent) => {
        this.fitSizes();
      });

    if (this.isPdf) {
      MyLog.log('IS PDF');

      /*
      const editType = [
        'agb',
        'receipt',
        'report',
        'acceptance'
      ].indexOf(this.data?.type) >= 0
        ? '/view'
        : '/edit';
        */

      const editType = '/view';

      const url = this.data.url + editType;

      this.isLoading = true;

      this.dataService.get(url).subscribe(
        (res: any) => {
          this.isLoading = false;
          this.setDocumentInfo(res);
          this.updateButtons();
        },
        (error: any) => {
          this.isLoading = false;
        }
      );
    }
  }

  ngOnDestroy(): void {
    this.udsSub = unsub(this.udsSub);
    this.removeVideoEL && this.removeVideoEL();
  }

  updateButtons() {
    if ('acceptance' === this.data?.type) {
      this.buttons = [
        {
          name: 'Kunde',
          page: 0,
        },
        {
          name: 'Schaden',
          page: 1,
        },
        {
          name: 'Unterschriften',
          page: 2,
        },
      ];
    }
  }

  viewPage(pageIdx: number) {
    MyLog.log('Page:', pageIdx);
    Number.isFinite(pageIdx) && this.loadPage(pageIdx);
  }

  setOriginalSize() {
    if (this.isVideo && this.videoHeight && this.videoWidth && this._video) {
      const elem = this._video.nativeElement;
      setElementSizes(elem, this.videoHeight, this.videoWidth);
    }
    if (
      this.isImageDoc &&
      this.imageWidth &&
      this.imageHeight &&
      this._imgView
    ) {
      const elem = this._imgView?.nativeElement;
      setElementSizes(elem, this.imageHeight, this.imageWidth);
    }
  }

  setElementPreviewSize(elem: any, h: any, w: any) {
    if (!elem) {
      return;
    }
    const dims = this.getPreviewSizes(h, w);
    setElementSizes(elem, dims.height, dims.width, 'px');
    //elem.style.height = dims.height;
    //elem.style.width = dims.width;
  }

  fitSizes() {
    if (this.isVideo && this.videoHeight && this.videoWidth && this._video) {
      const elem = this._video.nativeElement;
      MyLog.log('VS:', this.videoWidth, this.videoHeight);
      this.setElementPreviewSize(elem, this.videoHeight, this.videoWidth);
    }
    if (
      this.zoomMode === 1 &&
      this.isImageDoc &&
      this.imageWidth &&
      this.imageHeight &&
      this._imgView
    ) {
      const elem = this._imgView?.nativeElement;
      this.setElementPreviewSize(elem, this.imageHeight, this.imageWidth);
    }
  }

  getInnerContentSizes(): any {
    return getClientRect(this._content?.nativeElement);
  }

  getPreviewSizes(mediaHeight: any, mediaWidth: any) {
    const dims = this.getInnerContentSizes();

    let s = 1;

    if (dims) {
      MyLog.log('INPUT:', mediaHeight, mediaWidth, dims.height, dims.width);
      const newHeight = dims.height - 10;
      const newWidth = dims.width - 50;

      if (
        newHeight > 0 &&
        newWidth > 0 &&
        (mediaHeight > newHeight || mediaWidth > newWidth)
      ) {
        const scaleH = newHeight / mediaHeight;
        const scaleW = newWidth / mediaWidth;

        s = Math.min(scaleH, scaleW);

        mediaHeight = s * mediaHeight;
        mediaWidth = s * mediaWidth;
        MyLog.log('SCALE:', s, mediaHeight, mediaWidth);
      }
    }
    s = parseDec((s * 100).toFixed(0));
    this.scale = s;

    return {
      width: mediaWidth,
      height: mediaHeight,
    };
  }

  setZoomMode(mode: number) {
    if (this.zoomMode !== mode) {
      this.zoomMode = mode;

      if (mode === 1) {
        this.fitSizes();
      } else {
        this.setOriginalSize();
      }
    }
  }

  close() {
    this.dialogRef.close();
  }

  innerContent() {
    return document.getElementById('mcinnercontent');
  }

  setSizes() {
    const dialogDims = getDialogDims();
    const actionDims = getClientRect(ne(this._actions));
    const titleDims = getClientRect(ne(this._title));
    const innerContent = this.innerContent();

    if (
      innerContent &&
      actionDims &&
      actionDims.height > 0 &&
      titleDims &&
      dialogDims &&
      dialogDims.height > 0
    ) {
      let height = dialogDims.height - actionDims.height - titleDims.height - 40;
      height = Math.floor(height);

      if (height > 0) {
        elemHeight(innerContent, height + 'px');
        //innerContent.style.overflow = 'hidden';
      }
    }
  }

  loadPageSetData(pageIndex: number) {
    if (this.currentPage?.data) {
      this.imgUrl = 'data:image/png;base64,' + this.currentPage.data;
      return;
    }

    if (isNonEmptyString(this.currentPage?.url)) {
      this.imgUrl = '';

      this.imageLoading = true;

      setTimeout(() => {
        this.imgUrl = this.currentPage.url;
      }, 200);
    }
  }

  imageLoaded($event: any) {
    this.imageLoading = false;
  }



  imageLoadError($event: any) {
    console.error('ERR:', $event);
    this.imageLoadFailed = true;
    this.imageLoading = false;
  }
}
